// PRD 06 §2.1 — first card under the header. Hosts the hierarchical disease
// filter, the (read-only) monitoring-period display, and the scope-summary
// badges on the right.
//
// Monitoring period is FIXED at 26 weeks with no edit UI (ADM.002 / ADM.004) —
// it is shown read-only here (the value flows from the current configuration; BE pins 26).
//
// FEATURE_025 — choosing a row only changes what the page shows. It saves nothing,
// writes no audit row and does not light the 변경됨 chip, so a READ-only operator
// can use it too.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDiseaseOptionsQuery } from '../hooks/use-deployment-current-config';
import { buildScopeSummary } from '../utils/disease-scope';
import type { DiseaseFilterOption } from '../types/pro-deployment.types';

interface DiseaseScopeCardProps {
  diseaseFilter: string;
  monitoringWeeks: number;
  // Lifts the selected disease into page state so the choice survives the
  // refetch (the request is keyed off it).
  onDiseaseChange: (value: string) => void;
}

// PRD 06 §3.1 — group the flat option list by `group` while preserving order.
// Entries without `group` (ALL, COMMON) precede the optgroups.
function buildSelectGroups(options: DiseaseFilterOption[]): {
  topLevel: DiseaseFilterOption[];
  groups: { name: string; options: DiseaseFilterOption[] }[];
} {
  const topLevel: DiseaseFilterOption[] = [];
  const grouped = new Map<string, DiseaseFilterOption[]>();
  for (const opt of options) {
    if (!opt.group) {
      topLevel.push(opt);
      continue;
    }
    const bucket = grouped.get(opt.group) ?? [];
    bucket.push(opt);
    grouped.set(opt.group, bucket);
  }
  return {
    topLevel,
    groups: Array.from(grouped, ([name, options]) => ({ name, options })),
  };
}

export function DiseaseScopeCard({
  diseaseFilter,
  monitoringWeeks,
  onDiseaseChange,
}: DiseaseScopeCardProps) {
  const { t } = useTranslation('pro-deployment');
  const optionsQuery = useDiseaseOptionsQuery();

  const options = optionsQuery.data ?? [];
  const { topLevel, groups } = useMemo(() => buildSelectGroups(options), [options]);
  const summary = useMemo(() => buildScopeSummary(diseaseFilter, options), [diseaseFilter, options]);

  const handleDiseaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDiseaseChange(e.target.value);
  };

  return (
    <div className="card" style={{ padding: '20px 24px', marginBottom: 'var(--space-6)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <label
            htmlFor="mcDiseaseCode"
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: 'var(--foreground)',
            }}
          >
            {t('scope.diseaseLabel')}
          </label>
          <select
            id="mcDiseaseCode"
            className="input"
            style={{ minWidth: 280, height: 38, fontSize: 14 }}
            value={diseaseFilter}
            onChange={handleDiseaseChange}
            disabled={optionsQuery.isLoading}
          >
            {topLevel.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {groups.map((g) => (
              <optgroup key={g.name} label={g.name}>
                {g.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <label
            htmlFor="mcMonitorPeriod"
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: 'var(--foreground)',
            }}
          >
            {t('scope.weeksLabel')}
          </label>
          {/* ADM.002 / ADM.004 — fixed 26 weeks, no edit UI. Read-only display. */}
          <span
            id="mcMonitorPeriod"
            style={{
              minWidth: 72,
              textAlign: 'center',
              height: 38,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 14,
              color: 'var(--foreground)',
            }}
          >
            {monitoringWeeks}
          </span>
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--muted-foreground)' }}>
            {t('scope.weeksUnit')}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            marginLeft: 'auto',
          }}
        >
          {summary.kind === 'ALL' ? (
            <span
              className="badge"
              style={{ background: 'var(--secondary)', color: 'var(--secondary-foreground)' }}
            >
              {t('scope.summary.all')}
            </span>
          ) : null}
          {summary.kind === 'COMMON' ? (
            <span
              className="badge"
              style={{ background: 'var(--secondary)', color: 'var(--secondary-foreground)' }}
            >
              {t('scope.summary.common')}
            </span>
          ) : null}
          {summary.kind === 'GROUP' ? (
            <>
              <span
                className="badge"
                style={{ background: 'var(--secondary)', color: 'var(--secondary-foreground)' }}
              >
                {t('scope.summary.commonBadge')}
              </span>
              <span className="badge badge-primary">
                {t('scope.summary.groupBadge', { group: summary.group })}
              </span>
            </>
          ) : null}
          {summary.kind === 'SPECIFIC' ? (
            <>
              <span
                className="badge"
                style={{ background: 'var(--secondary)', color: 'var(--secondary-foreground)' }}
              >
                {t('scope.summary.commonBadge')}
              </span>
              <span className="badge badge-primary">
                {t('scope.summary.groupBadge', { group: summary.group })}
              </span>
              <span className="badge badge-success">
                {t('scope.summary.specificBadge', { label: summary.specificLabel })}
              </span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
